import React, { useState } from 'react';
import FetchAssignedBucket from '../services';

type Experiments = {
  [key: string]: Experiment;
};

type Experiment = {
  defaultBucket?: string;
  buckets?: string[];
  isLoading?: boolean;
  hasErrored?: boolean;
  assignedBucket?: string;
  experimentName?: string;
  identifier?: string;
};

const defaults = {
  apiTimeout: 2000,
  cacheMinutes: 15,
  environment: 'development',
  defaultBucket: 'control'
};

type BucketId = {
  experimentName: string;
  bucketName: string;
};

type GetAssignedBucketProps = {
  experimentName: string;
  possibleBuckets: string[];
} & Partial<typeof defaults>;

const defaultContext = {
  defaults: {
    ...defaults
  },
  experiments: {} as Experiments,
  UpdateAssignedBucketByExperimentName: (_: BucketId) => {},
  UpdateParentWithBucket: (_: {
    experimentName: string;
    bucketName: string | string[];
  }) => {},
  RegisterBucketsToParent: (_: string, __: string[]) => {},
  beforeExperimentBucketAssigned: (_: { experimentName: string }) => {},
  afterExperimentBucketAssigned: (_: BucketId) => {},
  onWhenComponentMount: (_: BucketId) => {},
  GetAssignedBucket: (_: GetAssignedBucketProps) => Promise.resolve('control')
};

const ExperimentContext = React.createContext(defaultContext);

const defaultProps = {
  defaults: defaults,
  beforeExperimentBucketAssigned: (_: { experimentName: string }) => {},
  afterExperimentBucketAssigned: (_: BucketId) => {},
  onWhenComponentMount: (_: BucketId) => {}
};

const ExperimentProvider: React.FC<React.PropsWithChildren<
  Partial<
    Pick<
      typeof defaultProps,
      | 'beforeExperimentBucketAssigned'
      | 'afterExperimentBucketAssigned'
      | 'onWhenComponentMount'
    >
  > &
    Omit<
      typeof defaultProps,
      | 'beforeExperimentBucketAssigned'
      | 'afterExperimentBucketAssigned'
      | 'onWhenComponentMount'
    >
>> = (props = defaultProps) => {
  const {
    defaults: propDefaults,
    children,
    afterExperimentBucketAssigned,
    beforeExperimentBucketAssigned,
    onWhenComponentMount
  } = props;

  const [experiments, setExperiments] = useState<Experiments>({});

  const getExperimentsWithPossibleBuckets = (
    experimentsToUpdate: Experiments,
    addBuckets: string[],
    experimentName: string
  ) => {
    return {
      ...experimentsToUpdate,
      [experimentName]: {
        ...(experimentsToUpdate[experimentName] || {}),
        experimentName,
        buckets: Array.from(
          new Set(
            (experimentsToUpdate[experimentName]?.buckets || []).concat(
              addBuckets,
              'control'
            )
          )
        )
      }
    };
  };

  const UpdateParentWithBucket: typeof defaultContext.UpdateParentWithBucket = ({
    bucketName,
    experimentName
  }) => {
    setExperiments(currExperiments =>
      getExperimentsWithPossibleBuckets(
        currExperiments,
        Array.isArray(bucketName) ? bucketName : [bucketName],
        experimentName
      )
    );
  };

  const RegisterBucketsToParent: typeof defaultContext.RegisterBucketsToParent = (
    experimentName,
    buckets
  ) => {
    if (!experimentName) throw new Error('missing experiment name');
    if (buckets?.length === 0) throw new Error('missing buckets');

    buckets.forEach(bucketName => {
      UpdateParentWithBucket({ bucketName, experimentName });
    });
  };

  const getUpdatedExperimentsWithAssignment = (
    experimentsToUpdate: Experiments,
    bucketName: string,
    experimentName: string
  ) => {
    return {
      ...experimentsToUpdate,
      [experimentName]: {
        ...experimentsToUpdate[experimentName],
        assignedBucket: bucketName
      }
    };
  };

  const UpdateAssignedBucketByExperimentName: typeof defaultContext.UpdateAssignedBucketByExperimentName = ({
    bucketName,
    experimentName
  }) => {
    setExperiments(currExperiments =>
      getUpdatedExperimentsWithAssignment(
        currExperiments,
        bucketName,
        experimentName
      )
    );
  };

  const GetAssignedBucket: typeof defaultContext.GetAssignedBucket = ({
    experimentName,
    possibleBuckets = [],
    defaultBucket = 'control',
    ...options
  }) => {
    if (beforeExperimentBucketAssigned) {
      beforeExperimentBucketAssigned({ experimentName });
    }

    return Promise.resolve(
      FetchAssignedBucket({ experimentName, ...{ ...defaults, ...options } })
    ).then(({ assignedBucket }) => {
      const bucketName = assignedBucket || defaultBucket;

      const newExperiments = getUpdatedExperimentsWithAssignment(
        getExperimentsWithPossibleBuckets(
          experiments,
          [...possibleBuckets, defaultBucket],
          experimentName
        ),
        bucketName,
        experimentName
      );
      setExperiments(newExperiments);

      if (afterExperimentBucketAssigned)
        afterExperimentBucketAssigned({ experimentName, bucketName });

      return bucketName;
    });
  };

  const value = {
    defaults: {
      ...defaults,
      ...propDefaults
    },
    experiments: experiments,
    UpdateAssignedBucketByExperimentName: UpdateAssignedBucketByExperimentName,
    UpdateParentWithBucket: UpdateParentWithBucket,
    RegisterBucketsToParent: RegisterBucketsToParent,
    beforeExperimentBucketAssigned:
      beforeExperimentBucketAssigned ??
      defaultContext.beforeExperimentBucketAssigned,
    afterExperimentBucketAssigned:
      afterExperimentBucketAssigned ??
      defaultContext.afterExperimentBucketAssigned,
    onWhenComponentMount:
      onWhenComponentMount ?? defaultContext.onWhenComponentMount,
    GetAssignedBucket: GetAssignedBucket
  };

  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
};

export default ExperimentContext;

export { ExperimentProvider };
